// src/pages/fleet/theme.js — Day / Night theme for the Fleet portal.
// Colours are CSS variables set on the shell's root element, so every page that
// styles with var(--fx-*) switches instantly without re-rendering its own code.
// Palette follows Apple's system colours (light / dark variants).
import React, { useEffect, useState } from 'react';

export const THEMES = {
  light: {
    '--fx-bg': '#F5F5F7',
    '--fx-surface': '#FFFFFF',
    '--fx-surface-2': '#F2F2F7',
    // A cool blue tint rather than plain grey. Enough colour to read as a
    // deliberate surface, light enough that the vivid icon tiles still carry
    // the eye — this is looked at all day, so it must not compete with them.
    '--fx-sidebar': 'rgba(214,227,250,0.94)',
    '--fx-sidebar-2': 'rgba(189,208,243,0.96)',   // the foot of the sidebar gradient
    // Labels ON the tinted sidebar need their own colours. The page's muted grey
    // (#AEAEB2) measures 1.7:1 against this blue — effectively invisible. These
    // clear 4.5:1 at both ends of the gradient.
    '--fx-sidebar-text': '#1B2435',
    '--fx-sidebar-text-2': '#3C4A61',
    '--fx-sidebar-text-3': '#475569',
    '--fx-border': 'rgba(0,0,0,0.07)',
    '--fx-border-strong': 'rgba(0,0,0,0.13)',
    '--fx-text': '#1D1D1F',
    '--fx-text-2': '#6E6E73',
    '--fx-text-3': '#AEAEB2',
    '--fx-accent': '#007AFF',
    '--fx-accent-soft': 'rgba(0,122,255,0.10)',
    '--fx-hover': 'rgba(0,0,0,0.04)',
    '--fx-input': '#FFFFFF',
    '--fx-green': '#34C759', '--fx-green-soft': 'rgba(52,199,89,0.13)',
    '--fx-red': '#FF3B30',   '--fx-red-soft': 'rgba(255,59,48,0.12)',
    '--fx-orange': '#FF9500','--fx-orange-soft': 'rgba(255,149,0,0.14)',
    '--fx-purple': '#AF52DE','--fx-purple-soft': 'rgba(175,82,222,0.12)',
    '--fx-gray-soft': 'rgba(142,142,147,0.14)',
    '--fx-shadow': '0 1px 2px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.05)',
    '--fx-shadow-lg': '0 20px 50px rgba(0,0,0,0.14)',
    '--fx-scrim': 'rgba(0,0,0,0.25)',
    colorScheme: 'light',
  },
  dark: {
    '--fx-bg': '#0E0E10',
    '--fx-surface': '#1C1C1E',
    '--fx-surface-2': '#2C2C2E',
    // The same idea after dark: a blue-black rather than a neutral one, so the
    // sidebar reads as a surface and not as a hole in the screen.
    '--fx-sidebar': 'rgba(23,33,60,0.92)',
    '--fx-sidebar-2': 'rgba(15,22,44,0.96)',
    '--fx-sidebar-text': '#EEF2FB',
    '--fx-sidebar-text-2': '#B6C2D8',
    '--fx-sidebar-text-3': '#9AA7BF',
    '--fx-border': 'rgba(255,255,255,0.08)',
    '--fx-border-strong': 'rgba(255,255,255,0.16)',
    '--fx-text': '#F5F5F7',
    '--fx-text-2': '#A1A1A6',
    '--fx-text-3': '#636366',
    '--fx-accent': '#0A84FF',
    '--fx-accent-soft': 'rgba(10,132,255,0.20)',
    '--fx-hover': 'rgba(255,255,255,0.06)',
    '--fx-input': '#2C2C2E',
    '--fx-green': '#30D158', '--fx-green-soft': 'rgba(48,209,88,0.18)',
    '--fx-red': '#FF453A',   '--fx-red-soft': 'rgba(255,69,58,0.18)',
    '--fx-orange': '#FF9F0A','--fx-orange-soft': 'rgba(255,159,10,0.18)',
    '--fx-purple': '#BF5AF2','--fx-purple-soft': 'rgba(191,90,242,0.18)',
    '--fx-gray-soft': 'rgba(142,142,147,0.20)',
    '--fx-shadow': '0 1px 2px rgba(0,0,0,0.3), 0 6px 20px rgba(0,0,0,0.25)',
    '--fx-shadow-lg': '0 20px 50px rgba(0,0,0,0.5)',
    '--fx-scrim': 'rgba(0,0,0,0.55)',
    colorScheme: 'dark',
  },
};

// San Francisco on Apple devices, Inter elsewhere.
export const FONT = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', 'Segoe UI', sans-serif";

const KEY = 'fleet-theme';
const systemDark = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

// 'light' | 'dark'. Starts from the saved choice, else the OS setting.
export type ThemeMode = keyof typeof THEMES;

export function useFleetTheme(): [ThemeMode, React.Dispatch<React.SetStateAction<ThemeMode>>] {
  const [mode, setMode] = useState<ThemeMode>(() => {
    try { const v = localStorage.getItem(KEY); if (v === 'light' || v === 'dark') return v; } catch { /* storage blocked */ }
    return systemDark() ? 'dark' : 'light';
  });
  useEffect(() => {
    try { localStorage.setItem(KEY, mode); } catch { /* storage blocked */ }
    // Keep the page behind the shell (overscroll, scrollbars) in step with the theme.
    document.body.style.background = THEMES[mode]['--fx-bg'];
    document.documentElement.style.colorScheme = mode;
    // Leaving the fleet portal must not leave the other portals tinted.
    return () => { document.body.style.background = ''; document.documentElement.style.colorScheme = ''; };
  }, [mode]);
  return [mode, setMode];
}
